import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AuthService } from "@core/auth.service";
import { AuthSyndicateService } from "@core/AuthServices/authSyndicate/auth-syndicate.service";
import { IonicModule, MenuController } from "@ionic/angular";
import { AnimationOptions, LottieComponent } from "ngx-lottie";
import { concatMap, of, Subject, switchMap, timer } from "rxjs";
import { TabRepositoryService } from "src/app/pages/tabs/tab-repository.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { InboxTitleNudgeComponent } from "../inbox-title-nudge/inbox-title-nudge.component";
import { HomecardsService } from "@core/AuthServices/homeCards/homecards.service";

@Component({
  selector: "app-notification",
  templateUrl: "./notification.component.html",
  styleUrls: ["./notification.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    LottieComponent,
    InboxTitleNudgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationComponent implements OnInit {
  ringBell$ = this.tabService.ringBell$;
  redDotUnread$ = this.tabService.redDotUnread$;
  options: AnimationOptions = {
    path: "/assets/notification-animation.json",
  };
  updateAnimationSubject = new Subject<{ location_id: string, delayTime: number }>();
  userId: string;
  @Input() inboxStatus: boolean;
  @Input() inboxOptions: AnimationOptions;
  @Input() inboxDuration: number;
  @Output() inboxView = new EventEmitter();
  constructor(
    private tabService: TabRepositoryService,
    private menu: MenuController,
    private GTMSegment: GTMSegmentService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private AuthSyndicateService: AuthSyndicateService,
    private homecardsService: HomecardsService

  ) { }

  async ngOnInit() {
    this.initUpdateAnimation();
    await this.authService.getUid("NotificationComponent").then((uid) => {
      this.userId = uid;
    })
  }

  initUpdateAnimation() {
    this.updateAnimationSubject
      .pipe(
        concatMap(({ location_id, delayTime }) =>
          timer(delayTime * 1000).pipe(
            switchMap(() => this.sendUpdateStatusFB(location_id))
          )
        )
      ).subscribe();
  }

  // Dismiss the animation when user clicks on it.
  updateAnimationStatus(location_id: string, delayTime: number) {
    if (location_id && delayTime > 0 && delayTime != null) {
      this.updateAnimationSubject.next({ location_id, delayTime });
    }
  }

  onClick(location_id: string) {
    this.sendUpdateStatusFB(location_id).subscribe();
  }

  sendUpdateStatusFB(location_id: string) {
    if (!this.userId) return of(null);

    if (this.inboxStatus != undefined) {
      return this.authService.getV2CloudFunction("updatemicroanimationv2", {
        "userId": this.userId,
        "locationIds": location_id,
        "status": this.inboxStatus
      });
    } else {
      return of(null);
    }
  }

  async showInbox() {
    this.authService.setShowTapAnimation(false);
    this.inboxView.emit();
    this.GTMSegment.GTMPopupInitialization("inboxView");
    this.inboxOpened();
    this.GTMSegment.HotJarEvent("click_inbox");
    this.tabService.setRingBell(false);
    this.tabService.setRedDotUnread(false);
    // Dismiss the title nudge immediately on open
    if (this.userId) {
      this.authService.getV2CloudFunction("updateinappnotificationv2", {
        userId: this.userId,
        status: false
      }).subscribe({
        error: (e) => {
          return e;
        }
      });
    }
    await this.menu.open("inboxMenu").catch((err) => { });
    this.authService.trackFeatureIfNotExists('open_inbox');
    this.homecardsService.updateAddToHomeCardStatus({
      event: "open_inbox",
      status: true,
    })
  }

  // this method is trigger inbox opened Event
  inboxOpened() {
    let startTime = new Date();
    this.authService.userInbox.subscribe((messages) => {
      if (messages?.length > 0) {
        const scrollDepth = this.calculateSectionScrollDepth()?.toFixed(2);

        let endTime = new Date();
        let dwellTime = endTime.getTime() - startTime.getTime();
        let formattedDwellTime = this.AuthSyndicateService.convertMillisecondsToTime(dwellTime);
        const read = messages?.filter((f) => f.viewed)?.length;
        const unread = messages?.filter((f) => !f.viewed)?.length;
        if (!isNaN(parseFloat(scrollDepth))) {
          const scrollDepthValue = parseFloat(scrollDepth);
          const body = {
            Read_Messages_Count: read,
            Unread_Messages_Count: unread,
            Dwell_Time: formattedDwellTime,
            Scroll_Depth: `${scrollDepthValue}%`,
          };
          this.GTMSegment.GTMPopupInitializationForInboxNew("Inbox Opened", body);
        }
      }
    });
  }

  calculateSectionScrollDepth() {
    const sectionElement = this.elementRef?.nativeElement?.querySelector('#targetSection');
    if (sectionElement) {
      const sectionHeight = sectionElement?.offsetHeight;
      const sectionTop = sectionElement?.getBoundingClientRect()?.top;
      const windowHeight = window?.innerHeight;

      // Calculate scroll depth relative to the section
      if (sectionTop >= 0 && sectionTop <= windowHeight) {
        const visibleHeight = Math.min(sectionHeight, windowHeight - sectionTop);
        return (visibleHeight / sectionHeight) * 100;
      } else {
        return 0;
      }
    }
  }
}
